import styled from "styled-components";
import { Link } from "react-router-dom";
import { tokens } from "../../../../../public/tokens";
import Button from "../../../../library/Button";
import Pagination from "../../../../library/Pagination";

const { colors, typography, globals, effects } = tokens;

export const ModalOverlay = styled.div({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(29, 29, 29, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
});

export const ModalContainer = styled.div({
    position: 'relative',
    backgroundColor: colors.surfaces['container-background'],
    borderRadius: globals['border-radius'],
    border: `1px solid ${colors.surfaces['border-divider']}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
    maxWidth: '684px',
    alignItems: 'flex-start',
    padding: globals['spacing-7'],
    display: 'flex',
    flexDirection: 'column',
    gap: globals['spacing-5'],

});

export const ModalHeader = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    alignSelf: 'stretch',
    gap: globals['spacing-3'],
});

export const ModalTitle = styled.div({
    fontSize: typography.fontSize['3xl-24px'],
    fontWeight: typography.fontWeights.bold,
    color: colors.text.primary,
    lineHeight: typography.lineHeights[110],
});

export const CloseButton = styled.button({
    position: 'absolute',
    right: '11.505px',
    top: '7.807px',
    background: 'none',
    border: 'none',
    color: colors.neutral.grey[900],
    fontSize: typography.fontSize['lg-14px'],
    fontWeight: typography.fontWeights.light,
});

export const ModalDescription = styled.p({
     alignSelf: 'stretch',
    color: colors.text.secondary,
    fontSize: typography.fontSize['lg-14px'],
    lineHeight: typography.lineHeights['110'],
});

export const FieldGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    alignSelf: 'stretch',
});

export const FieldLabel = styled.label({
    fontSize: typography.fontSize['lg-14px'],
    color: colors.text.primary,
});

export const TogglesRow = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    gap: globals['spacing-6'],

    '& > label': {
        flex: '1 0 0',
        width: 'auto',
    },
});

export const ErrorMessage = styled.div({
    alignSelf: 'stretch',
    color: colors.states.alert.default,
    fontSize: typography.fontSize['lg-14px'],
    lineHeight: typography.lineHeights['110'],
});

export const ModalFooter = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: globals['spacing-3'],
});

export const CancelButton = styled.button({
    display: 'flex',
    height: '35px',
    padding: `${globals['spacing-2']} ${globals['spacing-4']}`,
    justifyContent: 'center',
    alignItems: 'center',
    gap: globals['spacing-1-5'],
    borderRadius: globals['border-radius'],
    background: colors.states.alert.default,
    color: colors.neutral.white,
    border: 'none',
    cursor: 'pointer',
});

export const multiSelectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? colors.states.info.default : colors.surfaces['border-divider'],
        display: 'flex',
        minHeight: globals['spacing-10'],
        padding: `${globals['spacing-1']} ${globals['spacing-1-5']}`,
        alignItems: 'center',
        gap: globals['spacing-1-5'],
        alignSelf: 'stretch',
        borderRadius: globals['border-radius'],
        border: `1px solid ${colors.surfaces['border-divider']}`,
        background: colors.surfaces['container-background'],
        boxShadow: '0 0 0 1px rgba(163, 163, 163, 0.50)',
        ':hover': {
            borderColor: colors.states.info.default,
        },
    }),
    valueContainer: (base) => ({
        ...base,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        alignContent: 'center',
        padding: 0,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        alignItems: 'center',
    }),
    multiValue: (base) => ({
        ...base,
        display: 'flex',
        minHeight: '25px',
        padding: '3px 5px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: globals['spacing-2-5'],
        borderRadius: globals['border-radius'],
        background: colors.neutral.grey['200'],
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: colors.text.primary,
        fontSize: typography.fontSize['lg-14px'],
        fontWeight: typography.fontWeights.semibold,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: colors.text.primary,
        fontSize: typography.fontSize['lg-14px'],
        fontWeight: typography.fontWeights.light,
        ':hover': {
            backgroundColor: 'transparent',
            color: colors.text.primary,
        },
    }),
};

const cellText = {
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    lineHeight: typography.lineHeights["100"],
    color: colors.text.primary,
};

export const BulkProgressWrapper = styled.div({
    marginBottom: globals["spacing-6"],
});

export const GradebookActionButton = styled(Button)({
    height: "35px",
});

export const GradebookPaginationFooter = styled(Pagination)({
    borderTop: "none",
});

export const GradebookCardBlock = styled.div({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    background: colors.neutral.white,
    border: `1px solid ${colors.surfaces["border-divider"]}`,
    borderRadius: globals["border-radius"],
    overflow: "hidden",
});

export const StudentAvatar = styled.span({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: globals["spacing-7"],
    height: globals["spacing-7"],
    overflow: "hidden",
    borderRadius: "50%",
    background: colors.neutral.grey["200"],
    color: colors.text.secondary,
    flexShrink: 0,
    fontSize: typography.fontSize["lg-14px"],
});

const toneColors = {
    success: colors.states.success,
    alert: colors.states.alert,
    warning: colors.states.warning,
    info: colors.states.info,
    plum: { default: colors.brand["plum-orchid"]["500"] },
    neutral: { default: colors.text.secondary, 100: colors.neutral.grey["100"] },
};

const MODULE_STATUS_BACKGROUND = {
    pass: "rgba(22, 163, 74, 0.05)",
    fail: "rgba(196, 61, 81, 0.05)",
    exempt: "rgba(71, 72, 87, 0.05)",
    "not-enrolled": "rgba(227, 146, 25, 0.00)",
};

export const StyledTable = styled.table({
    width: "100%",
    border: "none",
    borderCollapse: "separate",
    borderSpacing: 0,
    ...cellText,
});

export const TableBookResponsive = styled.div({
    position: "relative",
    width: "100%",
    margin: 0,
    padding: 0,
});

export const TableBookScroll = styled.div({
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
});

export const TableBookScrollbarTrack = styled.div(({ $left, $right }) => ({
    position: "absolute",
    bottom: 0,
    left: `${$left}px`,
    right: `${$right}px`,
    height: "5px",
    overflowX: "auto",
    overflowY: "hidden",
}));

export const TableBookScrollbarInner = styled.div({
    height: "1px",
});

export const StyledHeaderRow = styled.tr({
    background: colors.neutral.white,
});

export const StyledHeaderCell = styled.th({
    ...cellText,
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["lg-14px"],
    color: colors.neutral.grey["800"],
    textTransform: "none",
    height: "34px",
    padding: `${globals["spacing-0"]} ${globals["spacing-2"]}`,
    border: "none",
    borderBottom: `1px solid ${colors.surfaces["border-divider"]}`,
    whiteSpace: "nowrap",
    textAlign: "left",
    verticalAlign: "middle",
    boxSizing: "border-box",
    overflow: "hidden",
});

export const SortableHeaderCell = styled(StyledHeaderCell)({
    cursor: "pointer",
    userSelect: "none",

    "&:hover": {
        color: colors.text.primary,
    },
});

export const HeaderLabel = styled.span(({ $active = false }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2-5"],
    padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
    margin: `0 calc(-1 * ${globals["spacing-2"]})`,
    borderRadius: globals["border-radius"],
    backgroundColor: $active ? colors.neutral.black : "transparent",
    color: $active ? colors.neutral.white : "inherit",
}));

export const SortIcon = styled.i(({ $active }) => ({
    fontSize: typography.fontSize["sm-10px"],
    color: $active ? colors.neutral.white : colors.neutral.grey["500"],
}));

export const CheckboxHeaderCell = styled(StyledHeaderCell)({
    padding: `${globals["spacing-0"]} ${globals["spacing-3"]}`,
    textAlign: "center",
});

export const StyledRow = styled.tr({
    height: "56px"
});

export const StyledCell = styled.td({
    ...cellText,
    height: "56px",
    padding: `${globals["spacing-2"]} ${globals["spacing-3"]}`,
    border: "none",
    borderBottom: `1px solid ${colors.surfaces["border-divider"]}`,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    boxSizing: "border-box",
    overflow: "hidden",
});

export const CheckboxCell = styled(StyledCell)({
    padding: `${globals["spacing-2"]} ${globals["spacing-3"]}`,
    textAlign: "center",
});

export const CheckboxCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
});

const stickyPosition = ({ $left, $right, $width, $edge }) => ({
    position: "sticky",
    zIndex: 3,
    background: colors.neutral.white,
    ...($left !== undefined ? { left: `${$left}px` } : {}),
    ...($right !== undefined ? { right: `${$right}px` } : {}),
    ...($width !== undefined ? { width: `${$width}px`, minWidth: `${$width}px`, maxWidth: `${$width}px` } : {}),
    ...($edge === "left" ? {
        borderRight: `1px solid ${colors.brand.blue["500"]}`,
        boxShadow: "4px 0 6px -2px rgba(0, 0, 0, 0.08)",
    } : {}),
    ...($edge === "right" ? {
        borderLeft: `1px solid ${colors.brand.blue["500"]}`,
        boxShadow: "-4px 0 6px -2px rgba(0, 0, 0, 0.08)",
    } : {}),
});

export const StickyHeaderCell = styled(StyledHeaderCell)(stickyPosition);
export const StickySortableHeaderCell = styled(SortableHeaderCell)(stickyPosition);
export const StickyCheckboxHeaderCell = styled(CheckboxHeaderCell)(stickyPosition);
export const StickyCell = styled(StyledCell)(stickyPosition);
export const StickyCheckboxCell = styled(CheckboxCell)(stickyPosition);

export const StudentCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    minWidth: 0,
    height: "39px",
    overflow: "hidden",
});

export const StudentName = styled(Link)({
    ...cellText,
    color: colors.text.link,
    fontWeight: typography.fontWeights.bold,
    textDecoration: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
});

export const OriginLink = styled(Link)({
    ...cellText,
    color: colors.text.link,
    fontWeight: typography.fontWeights.semibold,
    lineHeight: typography.lineHeights["110"],
    textDecoration: "none",
});

export const LatestIntakeText = styled.span({
    ...cellText,
    color: colors.text.primary,
    fontWeight: typography.fontWeights.semibold,
    lineHeight: typography.lineHeights["110"],
});

export const ModuleHeaderTitle = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: globals["spacing-1"],
    width: "100%",
});

export const ModuleHeaderLink = styled(Link)({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1"],
    color: colors.text.link,
    textDecoration: "none",
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["lg-14px"],
    whiteSpace: "nowrap",
    textBoxTrim: "trim-both",
    textBoxEdge: "cap alphabetic",
});

export const ModuleHeaderIcon = styled.i({
    fontSize: typography.fontSize["md-12px"],
    color: colors.text.link,
    flexShrink: 0,
});

export const ModuleHeaderName = styled.p({
    margin: 0,
    width: 0,
    minWidth: "100%",
    fontSize: typography.fontSize["xs-8px"],
    fontWeight: typography.fontWeights.regular,
    color: colors.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

export const ModuleHeaderCell = styled(StyledHeaderCell)({
    minWidth: "130px",
    borderRight: `1px solid ${colors.surfaces["border-divider"]}`,
});

export const ModuleCell = styled(StyledCell)(({ $status }) => ({
    height: "56px",
    padding: globals["spacing-1"],
    position: "relative",
    textAlign: "center",
    borderRight: `1px solid ${colors.surfaces["border-divider"]}`,
    background: MODULE_STATUS_BACKGROUND[$status] || "transparent",
}));

export const ModuleCellContent = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: globals["spacing-1"],
    width: "100%",
    height: "100%",
    minWidth: globals["spacing-24"],
});

export const ModuleCellCenter = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
});

export const ModuleIntakeLink = styled(Link)({
    position: "absolute",
    top: 0,
    left: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-0-5"],
    fontSize: typography.fontSize["xs-8px"],
    color: colors.text.link,
    textDecoration: "none",
    fontWeight: typography.fontWeights.semibold,
    padding: globals["spacing-1"]
});

export const ModuleMarkRow = styled.div({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2"],
});

export const ModuleMarkText = styled.span({
    fontWeight: typography.fontWeights.semibold,
    fontSize: typography.fontSize["md-12px"],
    color: colors.text.primary,
});

export const ConditionIcon = styled.i(({ $tone = "neutral" }) => ({
    fontSize: typography.fontSize["sm-10px"],
    color: toneColors[$tone]?.default || toneColors.neutral.default,
}));

export const ExemptToggleButton = styled.button(({ $tone = "neutral" }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2"],
    border: "none",
    background: "none",
    padding: "5px",
    margin: "-5px",
    fontSize: typography.fontSize["sm-10px"],
    fontWeight: typography.fontWeights.medium,
    color: toneColors[$tone]?.default || toneColors.neutral.default,
    cursor: "pointer",
}));

export const ConditionTag = styled.span(({ $tone = "neutral" }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1"],
    minHeight: "18px",
    maxHeight: "18px",
    padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
    borderRadius: globals["border-radius"],
    border: "none",
    background: "transparent",
    color: toneColors[$tone]?.default || toneColors.neutral.default,
    fontSize: typography.fontSize["sm-10px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
    cursor: "default",
}));

export const ConditionTagIcon = styled.i({
    fontSize: typography.fontSize["xs-8px"],
});

export const ExemptCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: globals["spacing-2"],
    width: "100%",
    height: "100%",
});

export const ExemptLabel = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-2"],
    color: colors.neutral.grey["600"],
    fontSize: typography.fontSize["sm-10px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
});

export const ExemptLabelIcon = styled.i({
    fontSize: typography.fontSize["xs-8px"],
    color: colors.neutral.grey["600"],
});

export const ResultBadge = styled.span(({ $tone = "neutral" }) => {
    if ($tone === "success") {
        return {
            display: "inline-flex",
            alignItems: "center",
            gap: globals["spacing-1"],
            padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
            borderRadius: globals["border-radius"],
            fontSize: typography.fontSize["sm-10px"],
            fontWeight: typography.fontWeights.semibold,
            background: toneColors.success.default,
            color: colors.neutral.white,
            whiteSpace: "nowrap",
            height: "18px",
        };
    }

    const tone = toneColors[$tone] || toneColors.neutral;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: globals["spacing-1"],
        padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
        borderRadius: globals["border-radius"],
        fontSize: typography.fontSize["sm-10px"],
        fontWeight: typography.fontWeights.semibold,
        background: $tone === "alert" ? "rgba(196, 61, 81, 0.1)" : "rgba(227, 146, 25, 0.1)",
        color: tone.default,
        whiteSpace: "nowrap",
        height: "18px",
    };
});

export const ActionCellContent = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: globals["spacing-2"],
});

export const ShadowedCalculateButton = styled(Button)({
    boxShadow: `${effects.effects.shadow.default} !important`,
});

export const NoRecordsMessage = styled.div({
    ...cellText,
    width: "100%",
    padding: globals["spacing-10"],
    textAlign: "center",
    color: colors.text.secondary,
});
